import React from 'react';

import {
  AppBar,
  Box,
  Drawer,
  Grid,
  IconButton,
  Stack,
  Switch,
  Toolbar,
  Tooltip,
  Slider,
  SvgIcon
} from '@mui/material';
import PropTypes from 'prop-types';
import { styled, useTheme } from '@mui/material/styles';
import { isEqual, isEmpty } from 'lodash';

import AddLinkIcon from '@mui/icons-material/AddLink';
import CloseIcon from '@mui/icons-material/Close';
import CoPresentIcon from '@mui/icons-material/CoPresent';
import DescriptionIcon from '@mui/icons-material/Description';
import EditLocationAltIcon from '@mui/icons-material/EditLocationAlt';
import ImageIcon from '@mui/icons-material/Image';
import LayersIcon from '@mui/icons-material/Layers';
import MapIcon from '@mui/icons-material/Map';
import ShareIcon from '@mui/icons-material/Share';
import TextSnippetOutlinedIcon from '@mui/icons-material/TextSnippetOutlined';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';

import AnnotationDirectButtonsComponent from '../AnnotationDirectButtonsComponent';
import DownloadLinksComponent from '../DownloadLinksComponent';
import InfoLayerSelection from '../InfoLayerSelection';
import IntroductionMapComponent from '../IntroductionMapComponent';
import MapLayerOrder from '../MapLayerOrder';
import MapShareTogether from '../MapShareTogether';
import NotesComponent from '../NotesComponent';
import ShowMapDeckLidarComponent from '../ShowMapDeckLidarComponent';
import PointMapComponent from '../PointMapComponent';
import ViewLinksComponent from '../ViewLinksComponent';
import SentinelComponent from '../SentinelComponent';

import { useStyles } from '../../../styles/mapStylesCustom';
import useTimeStore from '../ZustandProvider';

const NavBar = props => {
  const { changeLoading, isMobile, isPublicMap, map, MapProps } = props;
  const adjustMap = useTimeStore(state => state.adjustMap);
  const drawerWidth = useTimeStore(state => state.drawerWidth);
  const mapTypeView = useTimeStore(state => state.mapTypeView);
  const setAdjustMap = useTimeStore(state => state.setAdjustMap);
  const setDrawerWidth = useTimeStore(state => state.setDrawerWidth);
  const setGeometryConfig = useTimeStore(state => state.setGeometryConfig);

  const { settingsAdvancedMap } = map;

  const [componentToRender, setComponentToRender] = React.useState('');
  const [elementToRenderAux, setElementToRenderAux] = React.useState(
    settingsAdvancedMap.defaultComponent
  );
  const [featureProp, setFeatureProp] = React.useState({});
  const openLeftStart = drawerWidth !== 0;
  const [open, setOpen] = React.useState(openLeftStart);

  const drawerWidthLeft = `${drawerWidth - drawerWidth / 100}vw`;
  const drawerWidthLocal = isMobile ? '100%' : drawerWidthLeft;

  const heightLeft = isPublicMap ? 0 : '56px';
  const heightMobile = open ? heightLeft : '60%';
  const heightLocal = isMobile ? heightMobile : heightLeft;

  const classes = useStyles();
  const theme = useTheme();

  const marks = [
    {
      value: 25
    },
    {
      value: 100 / 3
    },
    {
      value: 50
    },
    {
      value: 75
    },
    {
      value: 100
    }
  ];

  const handleChange = (event, newValue) => {
    setDrawerWidth(newValue);
  };

  const handleDrawerBottom = () => {
    if (drawerWidth === 0) setDrawerWidth(100 / 3);
    setOpen(!open);
    if (open === true) {
      setOpen(false);
      setAdjustMap(false);
    } else {
      setOpen(true);
    }
  };

  function InfoSVG(propsAux) {
    return (
      <SvgIcon fontSize="inherit" style={{ width: 24, height: 24 }} {...propsAux}>
        <svg width="24" height="24" xmlns="http://www.w3.org/2000/svg">
          <g>
            <title>Layer 1</title>
            <rect
              stroke="#2A3F90"
              id="svg_3"
              height="0.11494"
              width="1.49425"
              y="58.4761"
              x="-0.52753"
              fill="#2A3F90"
            />
            <g id="svg_4">
              <rect
                stroke="#2A3F90"
                id="svg_1"
                height="10"
                width="3"
                y="9.9238"
                x="10.5"
                fill="#2A3F90"
              />
              <rect
                stroke="#2A3F90"
                id="svg_2"
                height="3"
                width="3"
                y="4.07621"
                x="10.5"
                fill="#2A3F90"
              />
            </g>
          </g>
        </svg>
      </SvgIcon>
    );
  }

  document.getElementsByTagName('body')[0].onclick = () => {
    const property = JSON.parse(localStorage.getItem('Propperties'));
    if (!isEqual(property, featureProp) && property !== null) {
      setFeatureProp({ ...property });
    }
    localStorage.removeItem('Propperties');
  };

  const DrawerHeader = styled('div')(() => ({
    display: 'flex',
    alignItems: 'center',
    ...theme.mixins.toolbar,
    justifyContent: 'flex-end'
  }));

  const renderComponent = async component => {
    if (!isEmpty(component)) {
      setOpen(true);
    }
    switch (component) {
      case 'AnnotationDirectButtons':
        setComponentToRender(
          <AnnotationDirectButtonsComponent isMobile={isMobile} MapProps={MapProps} />
        );
        break;
      case 'DownloadLinksComponent':
        setComponentToRender(<DownloadLinksComponent isMobile={isMobile} MapProps={MapProps} />);
        break;
      case 'ViewLinksComponent':
        setComponentToRender(<ViewLinksComponent isPublicMap={isPublicMap} MapProps={MapProps} />);
        break;
      case 'InfoLayerSelection':
        setComponentToRender(<InfoLayerSelection featureProp={featureProp} map={{ ...map }} />);
        break;
      case 'IntroductionMapComponent':
        setComponentToRender(
          <IntroductionMapComponent
            MapProps={MapProps}
            changeLoading={changeLoading}
            isMobile={isMobile}
          />
        );
        break;
      case 'LayersControl':
        setComponentToRender(<MapLayerOrder isPublicMap={isPublicMap} MapProps={MapProps} />);
        break;
      case 'NotesComponent':
        setComponentToRender(
          <NotesComponent
            isAnnotationsMaps
            isMobile={isMobile}
            notesProps={settingsAdvancedMap.notes}
            video={settingsAdvancedMap.videos}
          />
        );
        break;
      case 'SentinelComponent':
        setComponentToRender(<SentinelComponent />);
        break;
      case 'SharedComponent':
        setComponentToRender(<MapShareTogether isPublicMap={isPublicMap} map={{ ...map }} />);
        break;
      case 'ShowMapDeckLidarComponent':
        setComponentToRender(<ShowMapDeckLidarComponent />);
        break;
      case 'PointMapComponent':
        setComponentToRender(<PointMapComponent isPublicMap={isPublicMap} map={{ ...map }} />);
        break;
      default:
        setComponentToRender(false);
        break;
    }
    setElementToRenderAux(component);
  };

  React.useEffect(() => {
    renderComponent(elementToRenderAux);
  }, [map.shared, map.sharedPrivate, featureProp]);

  return (
    <>
      <AppBar
        position="static"
        style={{
          backgroundColor: 'rgb(251, 192, 45, 0)',
          color: '#2A3F90',
          height: '100%',
          overflow: 'hidden',
          radio: 15
        }}
        elevation={0}
      >
        <Toolbar
          style={{
            justifyContent:
              isMobile === true || drawerWidth >= 50 || mapTypeView === 'mapSync'
                ? 'flex-end'
                : 'center'
          }}
          disableGutters
        >
          <Stack
            spacing={1}
            direction={
              isMobile === true || drawerWidth >= 50 || mapTypeView === 'mapSync' ? 'column' : 'row'
            }
          >
            {settingsAdvancedMap.components.AnnotationDirectButtons.show &&
              (settingsAdvancedMap.showTooltip ? (
                <Tooltip title={settingsAdvancedMap.components.AnnotationDirectButtons.name} arrow>
                  <span>
                    <IconButton
                      className={classes.iconButtonStyle}
                      onClick={() => {
                        renderComponent('AnnotationDirectButtons');
                        setGeometryConfig('component');
                      }}
                    >
                      <EditLocationAltIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : (
                <IconButton
                  className={classes.iconButtonStyle}
                  onClick={() => {
                    renderComponent('AnnotationDirectButtons');
                    setGeometryConfig('component');
                  }}
                >
                  <EditLocationAltIcon />
                </IconButton>
              ))}
            {settingsAdvancedMap.components.IntroductionMapComponent.show &&
              (settingsAdvancedMap.showTooltip ? (
                <Tooltip title={settingsAdvancedMap.components.IntroductionMapComponent.name} arrow>
                  <span>
                    <IconButton
                      aria-label="IntroductionMapComponent"
                      onClick={() => {
                        renderComponent('IntroductionMapComponent');
                        setGeometryConfig('component');
                      }}
                      size="small"
                      className={classes.azulIpsilumButton}
                      style={{
                        backgroundColor: 'white',
                        border: '1px solid',
                        borderColor: '#2A3F90'
                      }}
                      variant="contained"
                    >
                      <CoPresentIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : (
                <IconButton
                  aria-label="IntroductionMapComponent"
                  onClick={() => {
                    renderComponent('IntroductionMapComponent');
                    setGeometryConfig('component');
                  }}
                  size="small"
                  className={classes.azulIpsilumButton}
                  style={{
                    backgroundColor: 'white',
                    border: '1px solid',
                    borderColor: '#2A3F90'
                  }}
                  variant="contained"
                >
                  <CoPresentIcon />
                </IconButton>
              ))}

            {settingsAdvancedMap.components.InfoLayerSelection.show &&
              (settingsAdvancedMap.showTooltip ? (
                <Tooltip title={settingsAdvancedMap.components.InfoLayerSelection.name} arrow>
                  <span>
                    <IconButton
                      aria-label="InfoIcon"
                      onClick={() => {
                        renderComponent('InfoLayerSelection');
                        setGeometryConfig('component');
                      }}
                      size="small"
                      className={classes.azulIpsilumButton}
                      style={{
                        backgroundColor: 'white',
                        border: '1px solid',
                        borderColor: '#2A3F90'
                      }}
                      variant="contained"
                    >
                      <InfoSVG />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : (
                <IconButton
                  aria-label="InfoIcon"
                  onClick={() => {
                    renderComponent('InfoLayerSelection');
                    setGeometryConfig('component');
                  }}
                  size="small"
                  className={classes.azulIpsilumButton}
                  style={{
                    backgroundColor: 'white',
                    border: '1px solid',
                    borderColor: '#2A3F90'
                  }}
                  variant="contained"
                >
                  <InfoSVG />
                </IconButton>
              ))}

            {settingsAdvancedMap.components.SentinelComponent.show &&
              (settingsAdvancedMap.showTooltip ? (
                <Tooltip title={settingsAdvancedMap.components.SentinelComponent.name} arrow>
                  <span>
                    <IconButton
                      aria-label="Shared"
                      onClick={() => {
                        renderComponent('SentinelComponent');
                        setGeometryConfig('component');
                      }}
                      size="small"
                      className={classes.azulIpsilumButton}
                      style={{
                        backgroundColor: 'white',
                        border: '1px solid',
                        borderColor: '#2A3F90'
                      }}
                      variant="contained"
                    >
                      <ImageIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : (
                <IconButton
                  aria-label="Shared"
                  onClick={() => {
                    renderComponent('SentinelComponent');
                    setGeometryConfig('component');
                  }}
                  size="small"
                  className={classes.azulIpsilumButton}
                  style={{
                    backgroundColor: 'white',
                    border: '1px solid',
                    borderColor: '#2A3F90'
                  }}
                  variant="contained"
                >
                  <ImageIcon />
                </IconButton>
              ))}
            {settingsAdvancedMap.components.SharedComponent.show &&
              (settingsAdvancedMap.showTooltip ? (
                <Tooltip title={settingsAdvancedMap.components.SharedComponent.name} arrow>
                  <span>
                    <IconButton
                      aria-label="Shared"
                      onClick={() => {
                        renderComponent('SharedComponent');
                        setGeometryConfig('component');
                      }}
                      size="small"
                      className={classes.azulIpsilumButton}
                      style={{
                        backgroundColor: 'white',
                        border: '1px solid',
                        borderColor: '#2A3F90'
                      }}
                      variant="contained"
                    >
                      <ShareIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : (
                <IconButton
                  aria-label="Shared"
                  onClick={() => {
                    renderComponent('SharedComponent');
                    setGeometryConfig('component');
                  }}
                  size="small"
                  className={classes.azulIpsilumButton}
                  style={{
                    backgroundColor: 'white',
                    border: '1px solid',
                    borderColor: '#2A3F90'
                  }}
                  variant="contained"
                >
                  <ShareIcon />
                </IconButton>
              ))}

            {settingsAdvancedMap.components.PointMapComponent.show &&
              (settingsAdvancedMap.showTooltip ? (
                <Tooltip title={settingsAdvancedMap.components.PointMapComponent.name} arrow>
                  <span>
                    <IconButton
                      aria-label="StreetView"
                      onClick={() => {
                        renderComponent('PointMapComponent');
                        setGeometryConfig('StreetView');
                      }}
                      size="small"
                      className={classes.azulIpsilumButton}
                      style={{
                        backgroundColor: 'white',
                        border: '1px solid',
                        borderColor: '#2A3F90'
                      }}
                      variant="contained"
                    >
                      <TravelExploreIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : (
                <IconButton
                  aria-label="StreetView"
                  onClick={() => {
                    renderComponent('PointMapComponent');
                    setGeometryConfig('StreetView');
                  }}
                  size="small"
                  className={classes.azulIpsilumButton}
                  style={{
                    backgroundColor: 'white',
                    border: '1px solid',
                    borderColor: '#2A3F90'
                  }}
                  variant="contained"
                >
                  <TravelExploreIcon />
                </IconButton>
              ))}

            {settingsAdvancedMap.components.LayersControl.show &&
              (settingsAdvancedMap.showTooltip ? (
                <Tooltip title={settingsAdvancedMap.components.LayersControl.name} arrow>
                  <span>
                    <IconButton
                      aria-label="LayersControl"
                      onClick={() => {
                        renderComponent('LayersControl');
                        setGeometryConfig('component');
                      }}
                      size="small"
                      className={classes.azulIpsilumButton}
                      style={{
                        backgroundColor: 'white',
                        border: '1px solid',
                        borderColor: '#2A3F90'
                      }}
                      variant="contained"
                    >
                      <LayersIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : (
                <IconButton
                  aria-label="LayersControl"
                  onClick={() => {
                    renderComponent('LayersControl');
                    setGeometryConfig('component');
                  }}
                  size="small"
                  className={classes.azulIpsilumButton}
                  style={{
                    backgroundColor: 'white',
                    border: '1px solid',
                    borderColor: '#2A3F90'
                  }}
                  variant="contained"
                >
                  <LayersIcon />
                </IconButton>
              ))}
            {settingsAdvancedMap.components.NotesComponent.show &&
              (settingsAdvancedMap.showTooltip ? (
                <Tooltip title={settingsAdvancedMap.components.NotesComponent.name} arrow>
                  <span>
                    <IconButton
                      aria-label="NotesComponent"
                      className={classes.azulIpsilumButton}
                      onClick={() => {
                        renderComponent('NotesComponent');
                        setGeometryConfig('component');
                      }}
                      size="small"
                      style={{
                        backgroundColor: 'white',
                        border: '1px solid',
                        borderColor: '#2A3F90'
                      }}
                      variant="contained"
                    >
                      <TextSnippetOutlinedIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : (
                <IconButton
                  aria-label="NotesComponent"
                  className={classes.azulIpsilumButton}
                  onClick={() => {
                    renderComponent('NotesComponent');
                    setGeometryConfig('component');
                  }}
                  size="small"
                  style={{
                    backgroundColor: 'white',
                    border: '1px solid',
                    borderColor: '#2A3F90'
                  }}
                  variant="contained"
                >
                  <TextSnippetOutlinedIcon />
                </IconButton>
              ))}
            {settingsAdvancedMap.components.DownloadLinksComponent.show &&
              (settingsAdvancedMap.showTooltip ? (
                <Tooltip title={settingsAdvancedMap.components.DownloadLinksComponent.name} arrow>
                  <span>
                    <IconButton
                      aria-label="DownloadLinksComponent"
                      className={classes.azulIpsilumButton}
                      onClick={() => {
                        renderComponent('DownloadLinksComponent');
                      }}
                      size="small"
                      style={{
                        backgroundColor: 'white',
                        border: '1px solid',
                        borderColor: '#2A3F90'
                      }}
                      variant="contained"
                    >
                      <AddLinkIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : (
                <IconButton
                  aria-label="DownloadLinksComponent"
                  className={classes.azulIpsilumButton}
                  onClick={() => {
                    renderComponent('DownloadLinksComponent');
                  }}
                  size="small"
                  style={{
                    backgroundColor: 'white',
                    border: '1px solid',
                    borderColor: '#2A3F90'
                  }}
                  variant="contained"
                >
                  <AddLinkIcon />
                </IconButton>
              ))}
            {settingsAdvancedMap.components.ShowMapDeckLidarComponent.show &&
              (settingsAdvancedMap.showTooltip ? (
                <Tooltip
                  title={settingsAdvancedMap.components.ShowMapDeckLidarComponent.name}
                  arrow
                >
                  <span>
                    <IconButton
                      aria-label="DownloadLinksComponent"
                      className={classes.azulIpsilumButton}
                      size="small"
                      style={{
                        backgroundColor: 'white',
                        border: '1px solid',
                        borderColor: '#2A3F90'
                      }}
                      variant="contained"
                      onClick={() => {
                        renderComponent('ShowMapDeckLidarComponent');
                        setGeometryConfig('component');
                      }}
                    >
                      <MapIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : (
                <IconButton
                  aria-label="DownloadLinksComponent"
                  className={classes.azulIpsilumButton}
                  size="small"
                  style={{
                    backgroundColor: 'white',
                    border: '1px solid',
                    borderColor: '#2A3F90'
                  }}
                  variant="contained"
                  onClick={() => {
                    renderComponent('ShowMapDeckLidarComponent');
                    setGeometryConfig('component');
                  }}
                >
                  <MapIcon />
                </IconButton>
              ))}
            {settingsAdvancedMap.components.ViewLinksComponent.show &&
              (settingsAdvancedMap.showTooltip ? (
                <Tooltip title={settingsAdvancedMap.components.ViewLinksComponent.name} arrow>
                  <span>
                    <IconButton
                      aria-label="ViewLinksComponent"
                      className={classes.azulIpsilumButton}
                      size="small"
                      style={{
                        backgroundColor: 'white',
                        border: '1px solid',
                        borderColor: '#2A3F90'
                      }}
                      variant="contained"
                      onClick={() => {
                        renderComponent('ViewLinksComponent');
                      }}
                    >
                      <DescriptionIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : (
                <IconButton
                  aria-label="ViewLinksComponent"
                  className={classes.azulIpsilumButton}
                  size="small"
                  style={{
                    backgroundColor: 'white',
                    border: '1px solid',
                    borderColor: '#2A3F90'
                  }}
                  variant="contained"
                  onClick={() => {
                    renderComponent('ViewLinksComponent');
                  }}
                >
                  <DescriptionIcon />
                </IconButton>
              ))}
          </Stack>
        </Toolbar>
      </AppBar>
      <Drawer
        sx={{
          width: drawerWidthLocal,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidthLocal,
            boxSizing: 'border-box',
            height: `calc(100% - ${heightLocal})`,
            top: heightLocal,
            bgcolor: 'white'
          }
        }}
        variant="persistent"
        anchor={isMobile ? 'bottom' : 'left'}
        open={open}
      >
        <DrawerHeader>
          <Grid item xs={12}>
            <Stack
              spacing={1}
              direction="row"
              justifyContent="space-around"
              alignItems="center"
              m={1}
              overflow-y="hidden"
            >
              <Grid item xs={4}>
                <Slider
                  aria-label="Restricted values"
                  value={drawerWidth}
                  step={null}
                  marks={marks}
                  valueLabelDisplay="auto"
                  onChange={handleChange}
                />
              </Grid>
              <Switch
                checked={adjustMap}
                onClick={() => {
                  setAdjustMap(!adjustMap);
                }}
                label={adjustMap === true ? 'Ajustar' : 'Block'}
                color="warning"
              />
              <IconButton onClick={handleDrawerBottom}>
                <CloseIcon />
              </IconButton>
            </Stack>
          </Grid>
        </DrawerHeader>
        {!isEmpty(componentToRender) ? (
          <Box color="black" bgcolor="white" word-wrap="break-word" height="100%">
            <div className="container-fluid">
              <br />
              {componentToRender}
            </div>
          </Box>
        ) : null}
      </Drawer>
    </>
  );
};

export default NavBar;

NavBar.defaultProps = {
  changeLoading: () => {},
  isMobile: false,
  isPublicMap: false,
  map: {},
  MapProps: {}
};
NavBar.propTypes = {
  changeLoading: PropTypes.func,
  isMobile: PropTypes.bool,
  isPublicMap: PropTypes.bool,
  map: PropTypes.object,
  MapProps: PropTypes.object
};
